import type { FC } from "react";

import { Alert } from "../../common/Alert";
import { Button } from "../../common/Button";
import { StepSuccessCard } from "./StepSuccessCard";

interface PaymentStepProps {
  isComplete: boolean;
  onSetupPayment: () => Promise<void>;
  onSkip: () => void;
  submitting: boolean;
  error: string | null;
}

export const PaymentStep: FC<PaymentStepProps> = ({
  isComplete,
  onSetupPayment,
  onSkip,
  submitting,
  error,
}) => {
  if (isComplete) {
    return (
      <StepSuccessCard
        title="Billing enabled"
        description="Billing profile configured. Add a payment method when you top up."
        onContinue={onSkip}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-mono text-sm font-bold uppercase tracking-widest text-white">
          Set up billing
        </h2>
        <p className="mt-1 text-sm leading-6 text-slate-400">
          Set up your billing profile to enable credit top-ups. You can add a
          payment method when you start your first top-up.
        </p>
      </div>

      {/* Pricing hint */}
      <div className="border border-slate-700 bg-arti-background p-3">
        <p className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">
          How pricing works
        </p>
        <div className="mt-2 space-y-1">
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs text-slate-400">
              Provider cost
            </span>
            <span className="font-mono text-xs text-white">Pass-through</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs text-slate-400">Arti fee</span>
            <span className="font-mono text-xs text-white">5%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="font-mono text-xs text-slate-400">Seat fees</span>
            <span className="font-mono text-xs text-emerald-400">None</span>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="error" animate>
          {error}
        </Alert>
      )}

      <Button
        onClick={() => void onSetupPayment()}
        loading={submitting}
        loadingText="Setting up..."
        fullWidth
      >
        Enable billing
      </Button>

      <Button onClick={onSkip} variant="ghost" fullWidth>
        Skip for now
      </Button>
    </div>
  );
};
